import { useState } from 'react'
import { COUNTRY_CODES, parseToE164, splitE164 } from '@/lib/phone'
import type { SupportedCountryCode } from '@/lib/phone'

// Reusable country-code + national-number phone input, built on the existing validated
// lib/phone.ts (libphonenumber-js) rather than a second, parallel phone representation.
// Wherever DriveTrack collects a phone number, it must be this combination — never a
// single free-text field.

interface PhoneFieldProps {
  label?: string
  /** Current stored value as E.164 (e.g. "+14165550142"), or null/empty if unset. */
  value: string | null
  /** Called with the composed E.164 string, or null if the national number is empty/invalid. */
  onChange: (e164: string | null) => void
  required?: boolean
  error?: string
}

export function PhoneField({ label = 'Phone', value, onChange, required, error }: PhoneFieldProps) {
  const parsed = value ? splitE164(value) : null
  const [countryCode, setCountryCode] = useState<SupportedCountryCode>(parsed?.countryCode ?? 'CA')
  const [national, setNational]       = useState(parsed?.national ?? '')

  const commit = (nextCountryCode: SupportedCountryCode, nextNational: string) => {
    setCountryCode(nextCountryCode)
    setNational(nextNational)
    onChange(parseToE164(nextNational, nextCountryCode))
  }

  return (
    <div>
      {label && <label className='field-label'>{label}</label>}
      <div style={{ display: 'flex', gap: 8 }}>
        <select
          value={countryCode}
          onChange={e => commit(e.target.value as SupportedCountryCode, national)}
          className='field-input'
          style={{ cursor: 'pointer', flex: '0 0 150px' }}
          aria-label='Country code'
        >
          {COUNTRY_CODES.map(c => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
        </select>
        <input
          type='tel'
          value={national}
          onChange={e => commit(countryCode, e.target.value)}
          placeholder='416 555 0142'
          required={required}
          className={`field-input${error ? ' error' : ''}`}
          style={{ flex: 1 }}
        />
      </div>
      {error && (
        <div style={{ font: '400 11.5px Inter,sans-serif', color: '#E0663B', marginTop: 5 }}>{error}</div>
      )}
    </div>
  )
}
